import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import './GlobalStyle.css'
import NotFound from '../../../Images/notFound.png';
import { Paper, makeStyles, TableBody, TableRow, TableCell, CircularProgress, Switch, Button } from '@material-ui/core';
import useTable from '../../useTable';
import DeleteIcon from '@material-ui/icons/Delete'
import CheckUserProvider from '../../../AllHooks/CheckUserProvider';
import { Helmet } from 'react-helmet';
import { deleteOrderMedicineForSupply, getAllSupplyOrderDocument } from '../../../Backend/firebase/supply-order';
import { parseDate } from '../../Models/CommonFunc';
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars';
import { useHistory } from 'react-router-dom';
import ChemistDropdown from './ChemistDropdown';
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import SupplyOrderFilter from './SupplyOrderFilter';


const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        width: '100%'
    },
    fixedHeight: {
        height: '100%',
    },
}));

const headCells = [
    { id: 'ChemistInfo', label: 'Chemist Info', disableSorting: false },
    { id: 'gstNo', label: 'GST No', disableSorting: true },
    { id: 'drugLicense', label: 'Drug License', disableSorting: true },
    { id: 'ProductName', label: 'Product Name', disableSorting: false },
    { id: 'MedicineType', label: 'Medicine Type', disableSorting: false },
    { id: 'ProductQuantity', label: 'Quantity', disableSorting: true },
    { id: 'ProductScheme', label: 'Scheme', disableSorting: true },
    { id: 'SupplyOrderPostedDateTime', label: 'Date', disableSorting: false },
    { id: 'action', label: 'Action', disableSorting: false },
]

function SupplyOrderHistory() {
    const classes = useStyles();
    const history = useHistory()
    const fixedHeightPaper = clsx(classes.paper, classes.fixedHeight);
    const { userData } = CheckUserProvider();

    const [records, setRecords] = useState([]);
    const [displayRecords, setDisplayRecords] = useState([]);
    const [SalectedDate, setSalectedDate] = useState(parseDate(new Date()));
    const [isLoading, setIsLoading] = useState(true);
    const { TblContainer, TblHead, TblPagination } = useTable(records, headCells);
    const [selectedChemist, setSelectedChemist] = useState("");
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');


    useEffect(async () => {
        var isLogin = localStorage.getItem(process.env.REACT_APP_EMPLOYER_ISLOGIN) || false;
        if (!isLogin || isLogin == "false") {
            history.push("/activity/Login");
        }
    }, [])

    const getLast7DaysDateRange = () => {
        const today = new Date();
        const last7Days = new Date();
        last7Days.setDate(today.getDate() - 7);

        return {
            start: parseDate(last7Days),
            end: parseDate(today),
        };
    };

    useEffect(async () => {
        if (userData) {
            setIsLoading(true);
            try {
                const { start, end } = getLast7DaysDateRange();
                const getSupplyOrder = await getAllSupplyOrderDocument();
                const todaysMedicine = getSupplyOrder.filter(x => {
                    const supplyDate = parseDate(x.SupplyOrderPostedDateTime);
                    return supplyDate >= start && supplyDate <= end;
                });
                setRecords(todaysMedicine?.sort((a, b) => a?.ProductName?.localeCompare(b?.ProductName)));
                setDisplayRecords(todaysMedicine?.sort((a, b) => a?.ProductName?.localeCompare(b?.ProductName)));
                setIsLoading(false);
            } catch (error) {
                console.log("Error Occur in Get Order", error)
                setIsLoading(false);
            }
        }
    }, [userData]);

    const handleFilters = async (filter) => {
        console.log('Filter Changed => ', filter);
    };

    useEffect(() => {
        const filteredRecords = records.filter(record => {
            const dateMatch = SalectedDate
                ? parseDate(record.SupplyOrderPostedDateTime).includes(SalectedDate)
                : true;

            const timeMatch = (startTime || endTime)
                ? (() => {
                    const recordTime = parseDate(record.SupplyOrderPostedDateTime, true).split(' ')[1]; // Extract time part only
                    if (startTime && endTime) {
                        return recordTime >= startTime && recordTime <= endTime;
                    } else if (startTime) {
                        return recordTime >= startTime;
                    } else if (endTime) {
                        return recordTime <= endTime;
                    }
                    return true;
                })()
                : true;

            const chemistMatch = selectedChemist
                ? record?.ChemistInfo?.toLowerCase()?.includes(selectedChemist.toLowerCase())
                : true;

            return dateMatch && timeMatch && chemistMatch;
        });

        setDisplayRecords(filteredRecords?.sort((a, b) => a?.ProductName?.localeCompare(b?.ProductName)));
    }, [SalectedDate, startTime, endTime, selectedChemist]); // Add startTime and endTime to the dependency array



    const convertToSlug = (input) => input?.toLowerCase()?.replace(/[^a-z0-9\s]/g, '')?.replace(/\s+/g, '_');

    // Common Function to Download Files
    const downloadXLSX = () => {
        // Group data by chemist unique identifier
        const groupedData = displayRecords.reduce((acc, item) => {
            const chemistId = item?.user?.uid || "NA";
            const date = parseDate(item?.SupplyOrderPostedDateTime);

            if (!acc[chemistId]) {
                acc[chemistId] = {
                    date,
                    chemistId,
                    records: [],
                };
            }

            acc[chemistId].records.push([
                item?.ProductName || "NA",
                item?.ProductQuantity || "NA",
                item?.ProductScheme || "NA",
                item?.MedicineType || "NA",
            ]);

            return acc;
        }, {});

        // Prepare Excel data
        const sheetData = [];
        Object.values(groupedData).forEach(({ date, chemistId, records }) => {
            sheetData.push(["Date", "Chemist Unique Identifier"]);
            sheetData.push([date, chemistId]);
            sheetData.push([]); // Empty row for spacing
            sheetData.push(["Product Name", "Quantity", "Scheme", "Medicine Type"]);
            sheetData.push(...records);
            sheetData.push([]); // Another empty row for separation
        });

        // Convert data to worksheet
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

        // Auto-adjust column width
        worksheet["!cols"] = [
            { wch: 20 }, // Date
            { wch: 30 }, // Chemist Unique Identifier
            { wch: 25 }, // Product Name
            { wch: 15 }, // Quantity
            { wch: 20 }, // Scheme
            { wch: 20 }, // Medicine Type
        ];

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

        // Convert workbook to binary data
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const excelBlob = new Blob([excelBuffer], { type: "application/octet-stream" });

        // Trigger download
        const fileName = selectedChemist
            ? `${convertToSlug(selectedChemist)}.xlsx`
            : "chemist_report.xlsx";
        saveAs(excelBlob, fileName);
    };


    // Function to download table data as a PDF in Table Format
    const downloadPDF = () => {
        const doc = new jsPDF();
        let yPos = 10; // Initial vertical position

        // Group data by chemist unique identifier
        const groupedData = displayRecords.reduce((acc, item) => {
            const chemistId = item?.user?.uid || "NA";
            const date = parseDate(item?.SupplyOrderPostedDateTime);

            if (!acc[chemistId]) {
                acc[chemistId] = {
                    date,
                    chemistId,
                    records: [],
                };
            }

            acc[chemistId].records.push([
                item?.ProductName || "NA",
                item?.ProductQuantity || "NA",
                item?.ProductScheme || "NA",
                item?.MedicineType || "NA",
            ]);

            return acc;
        }, {});

        Object.values(groupedData).forEach(({ date, chemistId, records }, index) => {
            if (index !== 0) doc.addPage(); // Add a new page for each chemist

            // Title for each section
            doc.setFontSize(12);
            doc.text("Chemist Order Report", 14, yPos);
            yPos += 8;

            // Chemist Info
            doc.setFontSize(10);
            doc.text(`Date: ${date}`, 14, yPos);
            yPos += 6;
            doc.text(`Chemist Unique Identifier: ${chemistId}`, 14, yPos);
            yPos += 10;

            // Table Headers
            const headers = [["Product Name", "Quantity", "Scheme", "Medicine Type"]];

            // Add table data
            doc.autoTable({
                startY: yPos,
                head: headers,
                body: records,
                theme: "grid",
                styles: { fontSize: 9 },
                headStyles: { fillColor: [22, 160, 133] }, // Teal color
                margin: { top: 10, left: 14, right: 14 },
            });

            yPos = doc.autoTable.previous.finalY + 10; // Move cursor down after table
        });

        const fileName = selectedChemist
            ? `${convertToSlug(selectedChemist)}.pdf`
            : "table_data.pdf";
        doc.save(fileName);
    };

    // Delete item from selectedItems
    const handleDeleteItem = (record) => {
        const isConfirmed = window.confirm(`Are you sure you want to delete ${record?.ProductName}?`);
        if (isConfirmed) {
            setRecords(displayRecords.filter(item => item._id !== record?._id));
            setDisplayRecords(displayRecords.filter(item => item._id !== record?._id));
            deleteOrderMedicineForSupply(record?._id);
        }
    };


    return (
        <>
            {!userData || isLoading ?
                (<div className="row">
                    <div className="col-md-12 text-center p-4">
                        <CircularProgress />
                    </div>
                </div>) :
                (<div className="allProductsAndSales">
                    <Paper className={fixedHeightPaper}>
                        <Helmet>
                            <meta name="description" content="Find Order you have posted till now." />
                            <title>Order | Kaipsool Pharmatech</title>
                        </Helmet>
                        <div className="row d-flex justify-content-center align-items-center">
                            <div className="col-md-6 inline-style">
                                <h5>Order List</h5>
                                <SupplyOrderFilter handleFilters={handleFilters} />
                            </div>
                            <div className="col-md-2">
                                <div className="allProductsTable__searchBox e-border-outline display-inline">
                                    <DatePickerComponent
                                        id="SalectedDate"
                                        name="SalectedDate"
                                        value={SalectedDate}
                                        cssclassName="e-outline"
                                        onChange={(e) => setSalectedDate(parseDate(e.target.value))}
                                        format="dd/MM/yyyy"
                                        cleared={() => {
                                            setTimeout(() => {
                                                setSalectedDate(parseDate(new Date()))
                                            }, 500)
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="allProductsTable__searchBox e-border-outline">
                                    {/* <label>Start Time</label> */}
                                    <input
                                        type="time"
                                        value={startTime}
                                        onChange={(e) => setStartTime(e.target.value)}
                                        className="e-outline"
                                    />
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="allProductsTable__searchBox e-border-outline">
                                    {/* <label>End Time</label> */}
                                    <input
                                        type="time"
                                        value={endTime}
                                        onChange={(e) => setEndTime(e.target.value)}
                                        className="e-outline"
                                    />
                                </div>
                            </div>
                            <div className="col-md-8">
                                <ChemistDropdown
                                    data={records}
                                    selectedChemist={selectedChemist}
                                    setSelectedChemist={setSelectedChemist}
                                />
                            </div>
                            <div className="col-md-4 mt-4 download-pdf-excel">
                                <Button
                                    onClick={downloadPDF}
                                    variant="contained"
                                    className="registerEmp__btnLogin jsmAdd"
                                >
                                    {'Export as PDF'}
                                </Button>
                                <Button
                                    onClick={downloadXLSX}
                                    variant="contained"
                                    className="registerEmp__btnLogin jsmAdd ml-4"
                                >
                                    {'Export as Excel'}
                                </Button>
                            </div>
                            {displayRecords.length > 0 ? <div className="row col-md-12 supply-order-table">
                                <TblContainer>
                                    <TblHead />
                                    <TableBody>
                                        {
                                            displayRecords.map(item => (
                                                <TableRow key={item.id}>
                                                    <TableCell>{item?.ChemistInfo ? item.ChemistInfo : "NA"}</TableCell>
                                                    <TableCell>{item?.user?.gstNo ? item.user.gstNo : "NA"}</TableCell>
                                                    <TableCell>{item?.user?.drugLicense ? item.user.drugLicense : "NA"}</TableCell>
                                                    <TableCell>{item?.ProductName ? item.ProductName : "NA"}</TableCell>
                                                    <TableCell>{item?.MedicineType ? item.MedicineType : "Generic"}</TableCell>
                                                    <TableCell>{item?.ProductQuantity ? item.ProductQuantity : "NA"}</TableCell>
                                                    <TableCell>{item?.ProductScheme ? item.ProductScheme : "NA"}</TableCell>
                                                    <TableCell>
                                                        {typeof item?.SupplyOrderPostedDateTime === "string"
                                                            ? item?.SupplyOrderPostedDateTime
                                                            : parseDate(item?.SupplyOrderPostedDateTime, true).toLocaleString()}
                                                    </TableCell>
                                                    <TableCell className="deleteCursor">
                                                        <DeleteIcon onClick={() => handleDeleteItem(item)} />
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        }
                                    </TableBody>
                                </TblContainer>
                            </div> : <div className="row col-md-12 mt-3 text-center">
                                <img src={NotFound} className="notFound" />
                                <h4>No Order Record Found!</h4>
                            </div>}
                        </div>
                    </Paper>
                </div>)
            }
        </>
    )
}

export default SupplyOrderHistory
